import React, { useState, useEffect } from "react";
import { FiCheckCircle, FiClock, FiAlertCircle, FiX, FiPackage } from "react-icons/fi";
import { createPortal } from "react-dom";
import axios from "../../../Utils/axiosInstance";
import toast from "react-hot-toast";

/**
 * ReturnDeliveredModal
 * Modal allowing supplier to confirm receipt of the return shipment lot.
 * Calls PATCH /return/:returnId/deliver
 */
const ReturnDeliveredModal = ({ isOpen, onClose, returnId, onSuccess }) => {
  const [animate, setAnimate] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setAnimate(true), 10);
    } else {
      setAnimate(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setAnimate(false);
    setTimeout(onClose, 250);
  };

  const handleConfirm = async () => {
    if (!returnId) return;
    setSubmitting(true);

    try {
      const res = await axios.patch(`/return/${returnId}/deliver`, {});
      if (res.data?.success) {
        toast.success(res.data.message || "Return package marked as delivered!");
        onSuccess?.();
        handleClose();
      } else {
        toast.error(res.data?.message || "Failed to mark as delivered");
      }
    } catch (err) {
      console.error("markReturnDelivered error:", err);
      toast.error(err?.response?.data?.message || "Failed to mark as delivered. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 transition-opacity duration-300 ${
        animate ? "opacity-100" : "opacity-0"
      }`}
      onClick={handleClose}
    >
      <div
        className={`bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden transition-all duration-300 ${
          animate ? "scale-100 translate-y-0" : "scale-95 translate-y-4"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-emerald-600 px-6 py-4 text-white flex items-center justify-between">
          <div className="flex items-center gap-2 font-bold text-base">
            <FiPackage className="text-xl" /> Confirm Return Delivery
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
          >
            <FiX className="text-lg" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-600 flex items-center justify-center mx-auto text-2xl shadow-xs">
            <FiCheckCircle />
          </div>

          <div>
            <h4 className="text-base font-bold text-gray-900">Package Arrived at Warehouse?</h4>
            <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
              Are you sure you have received this return package at your warehouse and want to mark it as delivered?
            </p>
          </div>

          <div className="p-3 bg-gray-50 rounded-xl border border-gray-100 text-xs text-gray-600">
            Once marked as delivered, the return order will be finalized and the return verification will be complete.
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              disabled={submitting}
              onClick={handleClose}
              className="flex-1 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-bold hover:bg-gray-50 transition text-xs"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={handleConfirm}
              className="flex-1 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold transition text-xs shadow-sm shadow-emerald-600/20"
            >
              {submitting ? "Confirming..." : "Yes, Mark Delivered"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ReturnDeliveredModal;
